import { useState } from "react";
import { api, useAuth } from "@/App";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function AgeVerificationModal({ isOpen, onClose, onVerified }) {
  const { checkAuth } = useAuth();
  const [dob, setDob] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!dob) {
      toast.error("Please enter your date of birth");
      return;
    }
    if (!confirmed) {
      toast.error("Please confirm you are 18 or older");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/verify-age", { date_of_birth: dob, confirm_18: confirmed });
      await checkAuth();
      toast.success("Age verified — you're all set!");
      onClose();
      if (onVerified) onVerified();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Verification failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="age-verification-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-primary" />
            Age Verification Required
          </DialogTitle>
          <DialogDescription>
            Premium date meetups are strictly for adults. Please verify your age to continue —
            this only takes a moment and is required once.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div>
            <label className="text-sm font-medium mb-1.5 block">Date of birth</label>
            <Input
              type="date"
              value={dob}
              onChange={(e) => setDob(e.target.value)}
              max={new Date().toISOString().split("T")[0]}
              data-testid="age-dob-input"
            />
          </div>

          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox
              checked={confirmed}
              onCheckedChange={(v) => setConfirmed(!!v)}
              className="mt-0.5"
              data-testid="age-confirm-checkbox"
            />
            <span className="text-sm text-muted-foreground leading-snug">
              I confirm that I am 18 years of age or older, and that the date of birth provided is accurate.
            </span>
          </label>

          <Button
            className="w-full"
            onClick={handleSubmit}
            disabled={submitting}
            data-testid="age-verify-submit"
          >
            {submitting ? "Verifying..." : "Verify My Age"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
